import { forwardRef, memo } from 'react';

import { Tally2Icon } from 'lucide-react';

import { cn } from '@/lib/utils';

import { ASSIGNMENT_TEXT_CLASSES } from '../../constants/assignment-text';

interface AssignmentPhantomProps {
  duration: number;
  assignmentTitle: string;
  textColor: string;
  phantomBackgroundColor: string;
  isResizing: 'left' | 'right' | false;
  isDragging: boolean;
  isDraft: boolean;
  isUnconfirmed: boolean;
  projectColorBorder: string;
  isOneDayAssignment: boolean;
}

export const AssignmentPhantom = memo(
  forwardRef<HTMLDivElement, AssignmentPhantomProps>(
    (
      {
        duration,
        assignmentTitle,
        textColor,
        phantomBackgroundColor,
        isResizing,
        isDragging,
        isDraft,
        isUnconfirmed,
        projectColorBorder,
        isOneDayAssignment,
      },
      ref
    ) => {
      return (
        <div
          ref={ref}
          className="assignment absolute top-0 z-[5] flex h-full items-center overflow-hidden rounded-[100px]"
          style={{
            left: '0',
            width: `${duration}rem`,
            pointerEvents: 'none',
            backgroundColor: phantomBackgroundColor,
            backgroundImage: isDraft
              ? `repeating-linear-gradient(
                -30deg,
                ${phantomBackgroundColor},
                ${phantomBackgroundColor} 3px,
                transparent 3px,
                transparent 6px
              )`
              : undefined,
            backgroundSize: isDraft ? `${duration}rem 28px` : undefined,
            border: isUnconfirmed ? `1px dashed ${projectColorBorder}` : undefined,
          }}
        >
          {isResizing === 'left' && (
            <Tally2Icon
              size={12}
              className="absolute -left-1 rotate-180"
              style={{
                color: textColor,
              }}
            />
          )}
          {/* Match assignment text structure: negative margin + full width + padding + inner truncate */}
          {/* Unconfirmed: skip phantom text since the underlying assignment text shows through */}
          {!isUnconfirmed && (
            <div
              className={cn(
                'w-[calc(100%+16px)] px-[12px] py-2 text-white',
                ASSIGNMENT_TEXT_CLASSES,
                {
                  // During drag: use absolute positioning to move with transform
                  // During resize: use sticky positioning to stay at viewport edge
                  absolute: isDragging || isResizing,
                  sticky: !(isDragging || isResizing),
                }
              )}
              style={{
                color: textColor,
              }}
            >
              <span className="flex min-w-0 items-center gap-0.5">
                <span className="truncate">{assignmentTitle}</span>
              </span>
            </div>
          )}
          {isResizing === 'right' && (
            <Tally2Icon
              size={12}
              className="absolute -right-1"
              style={{
                color: textColor,
              }}
            />
          )}
        </div>
      );
    }
  )
);

AssignmentPhantom.displayName = 'AssignmentPhantom';
